// ── SettingsTenantBrandingPage ─────────────────────────────────────────────
//
// Sprint BRAND BRAND.C2 (#840) — Settings → Branding management page.
// Logo upload wired in #2078 (BRAND.C1 #976's POST /logo was already fully
// implemented server-side; this page just stopped rendering it as disabled).
//
// Tenant-admin surface to edit the tenant's display name + two brand hex
// colours — the only fields tenant_branding holds (BRAND.A1 #834; there is no
// font/token column). Reads the resolved branding via
//   GET   /tenants/:tenantId/branding        (camelCase response, any member)
// and saves via
//   PATCH /tenants/:tenantId/branding        (TENANT_ADMIN; SNAKE_CASE body)
//   POST  /tenants/:tenantId/branding/logo   (TENANT_ADMIN; multipart, field "logo")
// Note the deliberate camelCase-GET / snake_case-PATCH asymmetry of the A1 API.
// Each colour is edited two ways, kept bidirectionally in sync: a hex text
// field (the source of truth, like ProjectBrandingOverrideSidebarCard / BRAND.A2)
// and a native <input type="color"> swatch that opens the OS palette. No
// colour-picker dependency is added — the native element matches the partner
// onboarding wizard's pattern. The picker's value is a derived read of the hex
// state and its onChange normalises the OS palette's lowercase #rrggbb back to
// the page's uppercase convention; it never writes on mount, so the
// only-changed-fields save guard below is untouched.
//
// Logo upload (#2078) — raw fetch() + FormData, cloned from
// SettingsCertificateInstitutionPage.tsx's uploadImage: apiFetch always sets
// Content-Type: application/json, which breaks multipart. Field name is
// literally "logo" (the server reads req.file from that name). The POST
// response is only {updated, logoMimeType} — NOT the full branding object —
// so a successful upload re-fetches GET /branding via apiFetch/applyBranding
// to refresh the displayed state.
//
// Logo DISPLAY (#2087) — GET /tenants/:tenantId/branding/logo now serves the
// bytes (BRAND.C1's storage always had them; #2078 just had nowhere to fetch
// them from). It's authenticated + membership-gated the same as every other
// branding endpoint, which means a plain <img src="..."> does NOT work: the
// JWT lives in localStorage and is attached per-request as a Bearer header,
// which browsers never do for image requests, and there's no general session
// cookie for a normal login (bp_action_session is magic-link-scoped only).
// So the logo is fetched via JS (same Bearer-token fetch as everything else
// on this page) and rendered through a blob URL instead — see
// fetchLogoImage below. If this ever gets "simplified" back to a bare <img>
// tag, it will fail silently (broken image, no console error to point at
// why) — don't do that without also changing the route's auth model.
//
// One acceptance criterion is still backend-blocked and handled as an honest
// stub:
//   • Revert-to-default → the PATCH can't null out fields (Zod requires hex;
//     the handler only sets provided fields), so a true server-side revert to
//     the BluePrint default isn't possible yet. The "Reset colours to BluePrint
//     defaults" button is a client-side convenience that fills the inputs; it
//     does NOT clear server overrides. Labelled honestly (follow-on filed).

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch, API_BASE } from '../lib/api';
import Sidebar from '../components/Sidebar';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';

const HEX_COLOUR = /^#[0-9A-Fa-f]{6}$/;
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

// Platform defaults — mirror apps/api BLUEPRINT_BRAND_DEFAULTS so the preview
// and the "reset colours" convenience match what the server falls back to.
const DEFAULT_PRIMARY = '#7C3AED';
const DEFAULT_SECONDARY = '#EC4899';
const DEFAULT_DISPLAY_NAME = 'BluePrint';

// GET response shape — the resolved (fallback-applied) branding context.
// displayName/primaryColor/secondaryColor are always concrete on the wire, but
// we tolerate null defensively. isCustom = the tenant has stored its own values.
interface BrandingResponse {
  displayName: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  logoMimeType: string | null;
  isCustom: boolean;
}

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-sm)',
  padding: 24,
  marginBottom: 16,
};

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
  color: 'var(--text)', marginBottom: 6, display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px',
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', outline: 'none',
};

// Native colour picker styled to read like the static swatch it replaces —
// same 28×28 box, border and radius. padding:0 plus the .branding-color-picker
// pseudo-element rules (index.css) strip the browser's default swatch inset so
// it renders as a flat filled square sitting where the preview span was.
const colorPickerStyle: React.CSSProperties = {
  width: 28, height: 28, flexShrink: 0, padding: 0,
  border: '1px solid var(--border)', borderRadius: 6,
  background: 'none', cursor: 'pointer',
};

const btnPrimary: React.CSSProperties = {
  padding: '8px 16px', fontFamily: 'var(--font-display)', fontSize: 13,
  fontWeight: 600, color: 'white', background: 'var(--primary)',
  border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
};

const btnGhost: React.CSSProperties = {
  padding: '8px 14px', fontFamily: 'var(--font-body)', fontSize: 13,
  fontWeight: 500, color: 'var(--text-secondary)', background: 'transparent',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

const hintStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)',
  display: 'block', marginTop: 4,
};

const errorBannerStyle: React.CSSProperties = {
  padding: '8px 12px', background: 'rgba(239,68,68,0.08)', color: 'var(--error)',
  borderRadius: 'var(--radius-sm)', marginBottom: 14, fontSize: 12,
};

export default function SettingsTenantBrandingPage() {
  const navigate = useNavigate();
  const { myTenant, loading: tenantLoading } = useMyTenant();
  const tenantId = myTenant?.tenantId;
  const isAdmin = isAdminRole(myTenant?.role);

  const [branding, setBranding] = useState<BrandingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // Logo-upload failures get their OWN error state, rendered inside the Logo
  // card itself (see #2117) — `error` above renders only in the Brand identity
  // card, at the top of the page. A user uploading a logo is scrolled down to
  // the Logo card, so a 415/400/413 set on the shared `error` state rendered
  // off-screen above was invisible in practice even though the DOM node (and
  // its text) were technically correct — a static/unit-test trace of the
  // branch logic can't catch that; it only shows up against real viewport
  // geometry. Don't merge this back into `error`.
  const [logoError, setLogoError] = useState('');
  const [logoObjectUrl, setLogoObjectUrl] = useState<string | null>(null);
  // Tracks the same value as logoObjectUrl, but read from a ref rather than
  // state so the unmount cleanup below can revoke it without calling setState
  // during teardown.
  const logoObjectUrlRef = useRef<string | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [secondaryColor, setSecondaryColor] = useState('');

  // Redirect anyone who is not a confirmed tenant admin to /settings (modeled
  // on SettingsOrganisationPage). Fail CLOSED: a null membership — e.g. the
  // /tenants/me/membership fetch failed and useMyTenant swallowed the error,
  // leaving myTenant null with loading cleared — also redirects, rather than
  // falling through toward the editable form. Editing is TENANT_ADMIN-only.
  useEffect(() => {
    if (!tenantLoading && !isAdmin) {
      navigate('/settings');
    }
  }, [tenantLoading, isAdmin, navigate]);

  // Apply a branding GET/PATCH response to local state, normalising the two hex
  // colours to the page's UPPERCASE convention. The colours can arrive lower-case
  // (legacy rows / an older client / the native picker on another surface);
  // surfacing them in convention casing means the field, the picker's
  // always-uppercase output, and the only-changed-fields diff all share one
  // casing — so the user never faces a confusing case-only "edit" and a no-op
  // re-pick of the identical colour is never persisted as a re-cased override.
  // displayName is left verbatim: names legitimately differ by case.
  const applyBranding = useCallback((b: BrandingResponse) => {
    const pc = b.primaryColor ? b.primaryColor.toUpperCase() : b.primaryColor;
    const sc = b.secondaryColor ? b.secondaryColor.toUpperCase() : b.secondaryColor;
    setBranding({ ...b, primaryColor: pc, secondaryColor: sc });
    setDisplayName(b.displayName ?? '');
    setPrimaryColor(pc ?? '');
    setSecondaryColor(sc ?? '');
  }, []);

  // Swap in a freshly-fetched object URL, revoking whatever was there before
  // (both the "replace an existing preview" and "clear back to no logo"
  // cases go through here, via url === null). Reading/writing the ref keeps
  // the unmount cleanup below revocation-safe without needing setState there.
  const setLogoPreview = useCallback((url: string | null) => {
    if (logoObjectUrlRef.current) URL.revokeObjectURL(logoObjectUrlRef.current);
    logoObjectUrlRef.current = url;
    setLogoObjectUrl(url);
  }, []);

  // Fetch the logo bytes (#2087) and render via a blob URL — see the file
  // header for why this can't be a plain <img src="...">. A missing logo
  // (404) is the ordinary empty state; any other failure (network blip, a
  // 401 mid-session, etc.) just falls back to no image rather than
  // surfacing an error banner — the logo preview is a nice-to-have, not
  // something that should break the rest of the card.
  const fetchLogoImage = useCallback(async (tid: string) => {
    const token = localStorage.getItem('blueprint_token');
    try {
      const res = await fetch(`${API_BASE}/tenants/${tid}/branding/logo`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) {
        setLogoPreview(null);
        return;
      }
      const blob = await res.blob();
      setLogoPreview(URL.createObjectURL(blob));
    } catch {
      setLogoPreview(null);
    }
  }, [setLogoPreview]);

  useEffect(() => {
    return () => {
      if (logoObjectUrlRef.current) URL.revokeObjectURL(logoObjectUrlRef.current);
    };
  }, []);

  const loadBranding = useCallback(() => {
    if (!tenantId) return;
    setLoading(true);
    setError('');
    apiFetch<BrandingResponse>(`/tenants/${tenantId}/branding`)
      .then(b => { applyBranding(b); void fetchLogoImage(tenantId); })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load branding'))
      .finally(() => setLoading(false));
  }, [tenantId, applyBranding, fetchLogoImage]);

  // Only admins can edit, and a non-admin is being redirected away — there's no
  // reason to fire the branding GET for them (it's any-member, but wasteful).
  // Gate the load on isAdmin so member sessions issue no /branding request.
  useEffect(() => { if (isAdmin) loadBranding(); }, [loadBranding, isAdmin]);

  const markDirty = () => { setSavedAt(null); setError(''); };

  const handleSave = async () => {
    if (!tenantId) return;
    setError('');

    // SNAKE_CASE body — the PATCH endpoint's documented shape. Send only the
    // fields the user actually CHANGED from what loaded. The GET returns the
    // RESOLVED branding (fallback-applied), so for a non-custom tenant the
    // colour inputs are pre-filled with the BluePrint defaults; sending those
    // unchanged would silently persist the defaults as explicit tenant
    // overrides (flipping isCustom on). Comparing against the loaded values
    // keeps a display-name-only edit from pinning the colours.
    const body: Record<string, string> = {};
    const dn = displayName.trim();
    const pc = primaryColor.trim();
    const sc = secondaryColor.trim();
    const loadedDn = branding?.displayName ?? '';
    const loadedPc = branding?.primaryColor ?? '';
    const loadedSc = branding?.secondaryColor ?? '';

    // Both the loaded baseline and the picker's output are in the page's
    // uppercase convention (applyBranding normalises colours on read), so a plain
    // equality diff suffices: an unchanged colour — including one re-picked
    // identically — compares equal and is not re-sent as an override.
    if (dn && dn !== loadedDn) body.display_name = dn;
    if (pc && pc !== loadedPc) {
      if (!HEX_COLOUR.test(pc)) { setError('Primary colour must be a 6-digit hex like #7C3AED'); return; }
      body.primary_color = pc;
    }
    if (sc && sc !== loadedSc) {
      if (!HEX_COLOUR.test(sc)) { setError('Secondary colour must be a 6-digit hex like #EC4899'); return; }
      body.secondary_color = sc;
    }

    if (Object.keys(body).length === 0) {
      setError('No changes to save.');
      return;
    }

    setSaving(true);
    try {
      const updated = await apiFetch<BrandingResponse>(`/tenants/${tenantId}/branding`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      applyBranding(updated);
      setSavedAt(Date.now());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Client-side convenience only — fills the colour inputs with the BluePrint
  // defaults. It does NOT clear server overrides (the API has no clear path
  // yet; see header). The user must still Save, which stores these as the
  // tenant's branding rather than reverting to inherit.
  const handleResetColours = () => {
    setPrimaryColor(DEFAULT_PRIMARY);
    setSecondaryColor(DEFAULT_SECONDARY);
    markDirty();
  };

  // Multipart POST /logo, cloned from SettingsCertificateInstitutionPage.tsx's
  // uploadImage: apiFetch can't be used here (it hardcodes
  // Content-Type: application/json, which breaks a FormData body), so this
  // hand-rolls fetch() with the same Authorization/credentials the rest of the
  // app uses. Only a size check runs client-side — format is deliberately NOT
  // pre-checked here, so a renamed file (e.g. an SVG saved as .png) reaches the
  // server's magic-byte check and gets the real 415 rather than a client guess.
  const handleLogoUpload = async (file: File) => {
    if (!tenantId) return;
    setLogoError('');

    if (file.size > LOGO_MAX_BYTES) {
      setLogoError(`File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 2 MB.`);
      return;
    }

    setUploadingLogo(true);
    try {
      const form = new FormData();
      form.append('logo', file);
      const token = localStorage.getItem('blueprint_token');
      const res = await fetch(`${API_BASE}/tenants/${tenantId}/branding/logo`, {
        method: 'POST',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        if (res.status === 400) throw new Error('No file selected.');
        if (res.status === 415) {
          throw new Error("That file isn't a PNG or JPEG. Note a renamed file won't work — the format is detected from the file itself.");
        }
        throw new Error(body.error || `Upload failed: HTTP ${res.status}`);
      }

      // The POST response is only {updated, logoMimeType} — re-fetch the full
      // resolved branding so the displayed name/colours/logo text stay in sync,
      // and re-fetch the image bytes so the preview updates too. The storage
      // key is deterministic per tenant+format (tenants/{id}/logo.<ext>), so
      // replacing a same-format logo overwrites the same key — this fetch
      // must not be skipped just because logoMimeType didn't change.
      const refreshed = await apiFetch<BrandingResponse>(`/tenants/${tenantId}/branding`);
      applyBranding(refreshed);
      void fetchLogoImage(tenantId);
    } catch (e) {
      setLogoError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Preview always renders something concrete: fall back to platform defaults
  // when the field is empty or an in-progress (invalid) hex.
  const previewPrimary = HEX_COLOUR.test(primaryColor.trim()) ? primaryColor.trim() : DEFAULT_PRIMARY;
  const previewSecondary = HEX_COLOUR.test(secondaryColor.trim()) ? secondaryColor.trim() : DEFAULT_SECONDARY;
  const previewName = displayName.trim() || DEFAULT_DISPLAY_NAME;

  // Native <input type="color"> needs a valid lowercase #rrggbb value. Derive
  // it from the same resolved preview value — the hex text field stays the
  // source of truth. This is a pure render-time read with NO write-back to
  // state, so mounting the picker never marks a field dirty and the #1373
  // only-changed-fields save guard is untouched. previewPrimary/previewSecondary
  // already fall back to the platform default for blank/partial hex, so the
  // native control is never handed an invalid value (no choke / reset).
  const pickerPrimary = previewPrimary.toLowerCase();
  const pickerSecondary = previewSecondary.toLowerCase();

  // ── Render ───────────────────────────────────────────────────────────────

  if (tenantLoading) {
    return (
      <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
        <Sidebar />
        <main className="flex-1 px-8 py-6">
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>Loading…</p>
        </main>
      </div>
    );
  }

  // Not a confirmed admin (a non-admin member OR a null/failed membership) —
  // the redirect effect is firing; render a minimal placeholder so the editable
  // form never renders to a non-admin. Fail closed.
  if (!isAdmin) {
    return (
      <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
        <Sidebar />
        <main className="flex-1 px-8 py-6">
          <p data-testid="branding-redirecting" style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>
            Redirecting…
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg)' }} data-testid="branding-page">
      <Sidebar />
      <main className="flex-1 px-8 py-6">
        <div style={{ marginBottom: 16 }}>
          <Link to="/settings" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}>
            ← Back to settings
          </Link>
        </div>

        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          Branding
        </h1>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, maxWidth: 640 }}>
          Set the display name and brand colours BluePrint applies to your generated artefacts. Only tenant admins can edit.
        </p>

        <div style={{ maxWidth: 640 }}>
          {loading ? (
            <div style={cardStyle}>
              <p data-testid="branding-loading" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                Loading branding…
              </p>
            </div>
          ) : (
            <>
              {/* Brand identity card */}
              <div style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                    Brand identity
                  </h2>
                  <span
                    data-testid="branding-status-badge"
                    style={{
                      fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
                      padding: '2px 8px', borderRadius: 'var(--radius-sm)',
                      color: branding?.isCustom ? 'var(--primary)' : 'var(--text-secondary)',
                      background: branding?.isCustom ? 'rgba(124,58,237,0.08)' : 'rgba(0,0,0,0.05)',
                    }}
                  >
                    {branding?.isCustom ? 'Custom branding' : 'Using BluePrint defaults'}
                  </span>
                </div>

                {error && (
                  <div data-testid="branding-error" style={errorBannerStyle}>{error}</div>
                )}
                {savedAt && !error && (
                  <div data-testid="branding-saved" style={{
                    padding: '8px 12px', background: 'rgba(16,185,129,0.08)', color: '#10b981',
                    borderRadius: 'var(--radius-sm)', marginBottom: 14, fontSize: 12,
                  }}>Branding saved.</div>
                )}

                {/* Display name */}
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle} htmlFor="branding-display-name">Display name</label>
                  <input
                    id="branding-display-name"
                    data-testid="branding-input-displayName"
                    type="text"
                    value={displayName}
                    onChange={e => { setDisplayName(e.target.value); markDirty(); }}
                    disabled={saving}
                    maxLength={255}
                    placeholder={DEFAULT_DISPLAY_NAME}
                    aria-describedby="branding-display-name-hint"
                    style={inputStyle}
                  />
                  <span id="branding-display-name-hint" style={hintStyle}>Shown on artefact headers and footers.</span>
                </div>

                {/* Primary colour — the picker + hex field edit one value, grouped
                    under the shared label so AT announces them as a pair. */}
                <div style={{ marginBottom: 16 }}>
                  <label id="branding-primary-color-label" style={labelStyle} htmlFor="branding-primary-color">Primary colour</label>
                  <div role="group" aria-labelledby="branding-primary-color-label" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* Native colour picker — opens the OS palette. Mirrors the
                        hex onChange exactly (uppercase to match the page's
                        convention + markDirty so a pick counts as a real edit);
                        the value is a derived read of the hex state, never the
                        reverse. */}
                    <input
                      id="branding-primary-color-picker"
                      data-testid="branding-picker-primary"
                      className="branding-color-picker"
                      type="color"
                      aria-label="Primary colour picker"
                      aria-describedby="branding-primary-color-hint"
                      value={pickerPrimary}
                      onChange={e => { setPrimaryColor(e.target.value.toUpperCase()); markDirty(); }}
                      disabled={saving}
                      style={colorPickerStyle}
                    />
                    <input
                      id="branding-primary-color"
                      data-testid="branding-input-primaryColor"
                      type="text"
                      value={primaryColor}
                      onChange={e => { setPrimaryColor(e.target.value); markDirty(); }}
                      disabled={saving}
                      placeholder="#RRGGBB"
                      aria-describedby="branding-primary-color-hint"
                      style={inputStyle}
                    />
                  </div>
                  <span id="branding-primary-color-hint" style={hintStyle}>6-digit hex, e.g. {DEFAULT_PRIMARY}.</span>
                </div>

                {/* Secondary colour — same grouped picker + hex field pattern. */}
                <div style={{ marginBottom: 16 }}>
                  <label id="branding-secondary-color-label" style={labelStyle} htmlFor="branding-secondary-color">Secondary colour</label>
                  <div role="group" aria-labelledby="branding-secondary-color-label" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* Native colour picker — see primary above for the sync
                        contract (derived value, uppercase + markDirty on pick). */}
                    <input
                      id="branding-secondary-color-picker"
                      data-testid="branding-picker-secondary"
                      className="branding-color-picker"
                      type="color"
                      aria-label="Secondary colour picker"
                      aria-describedby="branding-secondary-color-hint"
                      value={pickerSecondary}
                      onChange={e => { setSecondaryColor(e.target.value.toUpperCase()); markDirty(); }}
                      disabled={saving}
                      style={colorPickerStyle}
                    />
                    <input
                      id="branding-secondary-color"
                      data-testid="branding-input-secondaryColor"
                      type="text"
                      value={secondaryColor}
                      onChange={e => { setSecondaryColor(e.target.value); markDirty(); }}
                      disabled={saving}
                      placeholder="#RRGGBB"
                      aria-describedby="branding-secondary-color-hint"
                      style={inputStyle}
                    />
                  </div>
                  <span id="branding-secondary-color-hint" style={hintStyle}>6-digit hex, e.g. {DEFAULT_SECONDARY}.</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    type="button"
                    data-testid="branding-save"
                    onClick={() => void handleSave()}
                    disabled={saving}
                    style={{ ...btnPrimary, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
                  >
                    {saving ? 'Saving…' : 'Save branding'}
                  </button>
                  <button
                    type="button"
                    data-testid="branding-reset-colours"
                    onClick={handleResetColours}
                    disabled={saving}
                    style={btnGhost}
                  >
                    Reset colours to BluePrint defaults
                  </button>
                </div>
                <p data-testid="branding-reset-note" style={{ ...hintStyle, marginTop: 8 }}>
                  Reset fills the colour fields with BluePrint&apos;s defaults ({DEFAULT_PRIMARY} / {DEFAULT_SECONDARY}). You&apos;ll still need to Save — this stores the defaults as your branding and does not yet clear custom branding back to inherit (coming with custom branding support).
                </p>
              </div>

              {/* Live preview */}
              <div style={cardStyle}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                  Preview
                </h2>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                  A sample artefact header, updating as you edit.
                </p>
                <div
                  data-testid="branding-preview"
                  style={{
                    border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden',
                    background: '#ffffff',
                  }}
                >
                  {/* Header band — primary, with a secondary accent rule */}
                  <div style={{ background: previewPrimary, padding: '18px 20px' }}>
                    <div data-testid="branding-preview-name" style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: '#ffffff' }}>
                      {previewName}
                    </div>
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
                      Project Requirements Document
                    </div>
                  </div>
                  <div style={{ height: 4, background: previewSecondary }} />
                  {/* Mock body lines */}
                  <div style={{ padding: '18px 20px' }}>
                    <div style={{ height: 10, width: '40%', borderRadius: 3, background: previewSecondary, opacity: 0.85, marginBottom: 12 }} />
                    {['72%', '88%', '64%'].map((w, i) => (
                      <div key={i} style={{ height: 8, width: w, borderRadius: 3, background: '#e5e7eb', marginBottom: 8 }} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Logo — POST /tenants/:tenantId/branding/logo (BRAND.C1 #976)
                  to upload, GET .../logo (#2087) to display. PNG/JPEG only,
                  2 MiB max, validated server-side by magic bytes. The preview
                  below is a fetched blob, not a plain <img src> — see the
                  file header for why. */}
              <div style={cardStyle} data-testid="branding-logo-card">
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                  Logo
                </h2>
                {logoError && (
                  <div data-testid="branding-logo-error" style={{ ...errorBannerStyle, marginTop: 10 }}>{logoError}</div>
                )}
                {logoObjectUrl && (
                  <img
                    src={logoObjectUrl}
                    alt="Tenant logo"
                    data-testid="branding-logo-image"
                    style={{ maxWidth: 160, maxHeight: 80, display: 'block', marginBottom: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}
                  />
                )}
                <p data-testid="branding-logo-status" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
                  {branding?.logoMimeType
                    ? `A logo is on file (${branding.logoMimeType}).`
                    : 'No custom logo yet.'}
                </p>
                <label
                  htmlFor="branding-logo-file"
                  data-testid="branding-logo-upload"
                  style={{ ...btnGhost, display: 'inline-block', opacity: uploadingLogo ? 0.6 : 1, cursor: uploadingLogo ? 'not-allowed' : 'pointer' }}
                >
                  {uploadingLogo ? 'Uploading…' : branding?.logoMimeType ? 'Replace logo' : 'Upload logo'}
                </label>
                <input
                  id="branding-logo-file"
                  data-testid="branding-logo-file-input"
                  type="file"
                  accept="image/png,image/jpeg"
                  disabled={uploadingLogo}
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    e.target.value = ''; // allow re-selecting the same file
                    if (file) void handleLogoUpload(file);
                  }}
                />
                <span style={{ ...hintStyle, marginTop: 8 }}>
                  PNG or JPEG, up to 2 MB.
                </span>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
